import { RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { CheckIcon } from '@reverbdotcom/cadence/icons/react';
import { OverflowingRow } from '@reverbdotcom/commons';
import CmsListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import FilterChip from '@reverbdotcom/commons/src/components/filter_chip';
import { OverflowingRowAction } from '@reverbdotcom/commons/src/components/overflowing_row_action';
import { DEALS_AND_STEALS_CURATED_SET_ID } from '@reverbdotcom/commons/src/constants';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import experiments from '@reverbdotcom/commons/src/experiments';
import { HomepageDealsAndStealsListingsQuery, reverb_search_ListingsSearchRequest_PriceValue } from '@reverbdotcom/commons/src/rql';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { experimentInt } from '@reverbdotcom/commons/src/user_context_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import React, { useState } from 'react';
import { ChildProps } from 'react-apollo';
import URLHelpers from './shared/url_helpers';

type Props = ChildProps<{}, HomepageDealsAndStealsListingsQuery.Query>;

export function HomepageDealsAndStealsPersonalizedListings(props: Props) {
  const { data } = props;
  const user = useUser();
  const [selectedAffinity, setSelectedAffinity] = useState('ALL');

  const experiment = experiments.DEALS_AND_STEALS_HOME;
  const expGroup = experimentInt(user, experiment);
  const expDesc = formatExperimentsForEventAttributes(user, [experiment]);

  const topCategoryUuids = data?.me?.profile?.topCategoryUuids || [];
  const topCategories = (data?.me?.profile?.topCategories || []).reduce((memo, slug, idx) => {
    const uuid = topCategoryUuids[idx];
    const [product_type, category] = slug.split('/');
    memo[uuid] = {
      categoryName: I18n.t(`discovery.taxonomy.categories.${uuid}`),
      category,
      slug,
      product_type,
    };
    return memo;
  }, { 'ALL': { categoryName: I18n.t('discovery.taxonomy.allCategories') } });

  const eligible = !data.loading && topCategoryUuids.length > 0;

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'HomepageDealsAndStealsListingsCollection',
    experiments: expDesc,
  }, eligible);

  const setAndTrackSelectedAffinity = (typeUuid, categoryName) => {
    trackEvent({
      eventName: MParticleEventName.ClickedDealsFilterChip,
      componentName: 'FilterChip',
      experiments: expDesc,
      category: categoryName,
    });

    setSelectedAffinity(typeUuid);
  };

  const topAffinities = topCategoryUuids.slice(0, 3);

  if (expGroup === 1) {
    const categoryInfo = topCategories[topAffinities[0]];
    const dealsAndStealsPath = URLHelpers.marketplacePath({ product_type: categoryInfo?.product_type, category: categoryInfo?.category, deals_and_steals: true });

    return (
      <CmsListingsCollection
        data-deals-and-steals-listings
        curatedSetId={DEALS_AND_STEALS_CURATED_SET_ID}
        componentName="HomepageDealsAndSteals"
        trackingName="HomepageDealsAndSteals"
        ctaTargetHref={dealsAndStealsPath}
        categoryUuids={topAffinities}
        priceValueV2={reverb_search_ListingsSearchRequest_PriceValue.GOOD}
        title={I18n.t('discovery.homepage.dealsAndStealsForYou')}
        maxCount={12}
        largeTiles
        ssr={false}
      />
    );
  } else if (expGroup === 2) {
    const categoryInfo = topCategories[selectedAffinity];
    const dealsAndStealsPath = URLHelpers.marketplacePath({ product_type: categoryInfo?.product_type, category: categoryInfo?.category, deals_and_steals: true });

    const filterChips = [...topAffinities, 'ALL'].map((typeUuid, _) => {
      const selected = selectedAffinity === typeUuid;
      const { categoryName, slug } = topCategories[typeUuid];

      return (
        <div key={`homepage-deals-steals-filter-${slug || 'all'}`} className="d-inline-block mr-1">
          <FilterChip
            selected={selected}
            onClick={() => setAndTrackSelectedAffinity(typeUuid, slug)}
            key={`selected-product-affinity-category-${typeUuid}`}
          >
            {
              selected ? (
                <RCTextWithIcon svgComponent={CheckIcon} placement="left">
                  {categoryName}
                </RCTextWithIcon>
              ) : (
                categoryName
              )
            }
          </FilterChip>
        </div>
      );
    });

    return (
      <div className="homepage-container__component-wrapper">
        <div
          data-deals-and-steals-wrapper
          className="homepage-deals-steals-for-you__component-wrapper homepage-just-listed-for-you__component-wrapper-variant-2"
        >
          <OverflowingRow
            data-deals-and-steals-filters
            action={
              <OverflowingRowAction
                href={dealsAndStealsPath}
                text="See More"
              />
            }
            title={I18n.t('discovery.homepage.dealsAndStealsForYou')}
          >
            <div className="homepage-deals-steals-for-you__filters-component homepage-just-listed-for-you__filters-component">
              {filterChips}
            </div>
          </OverflowingRow>
          <CmsListingsCollection
            data-deals-and-steals-listings
            curatedSetId={DEALS_AND_STEALS_CURATED_SET_ID}
            componentName="HomepageDealsAndSteals"
            trackingName="HomepageDealsAndSteals"
            ctaTargetHref={dealsAndStealsPath}
            categoryUuids={selectedAffinity != 'ALL' ? [selectedAffinity] : topAffinities}
            priceValueV2={reverb_search_ListingsSearchRequest_PriceValue.GOOD}
            maxCount={12}
            largeTiles
            ssr={false}
          />
        </div>
      </div>
    );
  }

  return null;
}

const withQuery = withGraphql<Props, HomepageDealsAndStealsListingsQuery.Query>(
  gql`
    query Homepage_Deals_And_Steals_Listings_Query {
      me {
        uuid
        profile {
          topCategories
          topCategoryUuids
        }
      }
    }
  `,
);

export const HomepageDealsAndStealsPersonalizedListingsCollection = withQuery(HomepageDealsAndStealsPersonalizedListings);
